// src/data/wordSelector.js

/**
 * gewünschte Verteilung nach Wortarten
 */
const POS_RATIOS = {
  noun: 0.35,      // Nomen
  verb: 0.25,      // Verben
  adjective: 0.15, // Adjektive
  adverb: 0.1,     // Adverbien
  other: 0.15,     // Rest (Artikel, Pronomen, etc.)
};

const POS_ORDER = ["noun", "verb", "adjective", "adverb", "other"];

// Zufallsanteil kommt nur aus den nächsten RANDOM_WINDOW noch nicht gelernten Wörtern
// (anteilig je Wortart nach POS_RATIOS), damit nicht schon in Woche 1 ein Wort von Rang 2800 auftaucht.
const RANDOM_WINDOW = 200;

/* ---------- Hilfsfunktionen ---------- */

// zufällig n Elemente holen
function pickRandom(arr, n) {
  if (n <= 0) return [];
  if (n >= arr.length) return [...arr];

  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy.slice(0, n);
}

// Shuffle
function shuffleArray(arr) {
  const copy = [...arr];
  for (let i = copy.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

// Fallback: Mapping von type (deutsch) auf pos
function mapTypeToPosFallback(type) {
  switch (type) {
    case "substantiv":
      return "noun";
    case "verb":
      return "verb";
    case "adjektiv":
      return "adjective";
    case "adverb":
      return "adverb";
    default:
      return "other";
  }
}

/**
 * Hole den Vokabel-Pool für eine gegebene Sprachrichtung.
 * Der Pool kommt immer über vocabOverride (von loadVocabPair geladen) - diese Funktion
 * kennt keine konkreten Sprachen mehr, nur noch die generische Wort-Objektform.
 */
function getVocabForLanguage(languageId, vocabOverride) {
  if (vocabOverride && Array.isArray(vocabOverride)) {
    return vocabOverride;
  }
  return [];
}

/* ---------- Hauptfunktion: Wochenset auswählen ---------- */

/**
 * selectWeeklyWords:
 * - nutzt POS_RATIOS für die Verteilung
 * - pro POS: 85% Top (nach freqRankType), 15% random aus dem nächsten Häufigkeitsfenster
 * - keine Dopplungen
 * - akzeptiert optional:
 *    - vocabOverride (z. B. CSV)
 *    - excludedIds (Set oder Array von IDs, die NICHT mehr vorkommen dürfen)
 */
export function selectWeeklyWords({
  languageId,
  pace,
  totalWords,
  vocabOverride,
  excludedIds,
}) {
  const rawVocab = getVocabForLanguage(languageId, vocabOverride);
  if (!rawVocab || rawVocab.length === 0) return [];

  const total = Math.min(totalWords || 50, rawVocab.length);
  const languageKey = languageId || "lang";

  // excludedIds in Set konvertieren (kann Set oder Array sein)
  const excludedSet = new Set(
    excludedIds
      ? excludedIds instanceof Set
        ? Array.from(excludedIds)
        : excludedIds
      : []
  );

  // Normalize / Metadaten ergänzen
  const vocabWithMeta = rawVocab.map((w, index) => {
    const id = w.id || `${languageKey}_${index}`;

    // POS: entweder schon vorhanden (CSV) oder aus type ableiten
    const pos = w.pos || mapTypeToPosFallback(w.type) || "other";

    // Rang innerhalb der Wortart:
    const freqRankType =
      typeof w.freqRankType === "number" && !Number.isNaN(w.freqRankType)
        ? w.freqRankType
        : index + 1;

    // globaler Rang (falls später gebraucht)
    const freqRank =
      typeof w.freqRank === "number" && !Number.isNaN(w.freqRank)
        ? w.freqRank
        : index + 1;

    return {
      ...w,
      id,
      pos,
      freqRankType,
      freqRank,
    };
  });

  // Nach Wortarten aufsplitten
  const byPos = {
    noun: [],
    verb: [],
    adjective: [],
    adverb: [],
    other: [],
  };

  for (const w of vocabWithMeta) {
    const p = byPos[w.pos] ? w.pos : "other";
    byPos[p].push(w);
  }

  // Anzahl Zielwörter pro POS
  const plannedCounts = {};
  let sumPlanned = 0;

  POS_ORDER.forEach((pos, index) => {
    if (index < POS_ORDER.length - 1) {
      const count = Math.round(total * (POS_RATIOS[pos] || 0));
      plannedCounts[pos] = count;
      sumPlanned += count;
    } else {
      // letzte Kategorie bekommt den Rest
      plannedCounts[pos] = total - sumPlanned;
    }
  });

  // 85% Top / 15% Random pro POS
  const SEQUENTIAL_SHARE = 0.85;

  // usedIds startet bereits MIT excludedIds, damit diese NIE ausgewählt werden
  const usedIds = new Set(excludedSet);
  let selected = [];

  POS_ORDER.forEach((pos) => {
    const pool = byPos[pos];
    if (!pool || pool.length === 0) return;

    const targetForPos = plannedCounts[pos] || 0;
    if (targetForPos <= 0) return;

    const seqCount = Math.floor(targetForPos * SEQUENTIAL_SHARE);
    const randCount = targetForPos - seqCount;

    // Pool nach freqRankType sortieren (1 = am häufigsten in dieser Wortart)
    const sortedPool = [...pool].sort(
      (a, b) => (a.freqRankType || 9999) - (b.freqRankType || 9999)
    );

    // 1) Top-Anteil sequentiell, aber ohne already used/excluded
    const sequential = [];
    for (let i = 0; i < sortedPool.length && sequential.length < seqCount; i++) {
      const w = sortedPool[i];
      if (!w.id || usedIds.has(w.id)) continue;
      const wTop = { ...w, selectionSource: "top" };
      sequential.push(wTop);
      usedIds.add(wTop.id);
    }

    // 2) Random-Anteil aus dem nächsten Häufigkeitsfenster, wieder ohne already used/excluded
    const windowSize = Math.max(
      randCount,
      Math.round(RANDOM_WINDOW * (POS_RATIOS[pos] || 0))
    );
    const remainingPool = sortedPool
      .filter((w) => w.id && !usedIds.has(w.id))
      .slice(0, windowSize);
    const randomRaw = pickRandom(remainingPool, randCount);
    const random = randomRaw.map((w) => ({ ...w, selectionSource: "random" }));
    random.forEach((w) => usedIds.add(w.id));

    selected.push(...sequential, ...random);
  });

  // Falls wir durch Rundungen / leere Kategorien noch nicht genug Wörter haben:
  if (selected.length < total) {
    const need = total - selected.length;

    const remainingAll = vocabWithMeta
      .filter((w) => w.id && !usedIds.has(w.id))
      .sort(
        (a, b) =>
          (a.freqRank || a.freqRankType || 9999) -
          (b.freqRank || b.freqRankType || 9999)
      );

    const extraRaw = remainingAll.slice(0, need);
    const extra = extraRaw.map((w) => ({ ...w, selectionSource: "fallback" }));
    extra.forEach((w) => usedIds.add(w.id));
    selected.push(...extra);
  }

  // zur Sicherheit auf max. total beschränken
  if (selected.length > total) {
    selected = selected.slice(0, total);
  }

  // leicht mischen, damit es nicht stumpf nach POS sortiert ist
  return shuffleArray(selected);
}
